'use client';

/**
 * TagadaProvider - Main provider component for the Tagada Pay React SDK
 */

import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactNode, Suspense, createContext, lazy, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { ApiService } from '../../../react/services/apiService';
import {
  convertCurrency,
  formatMoney,
  formatMoneyWithoutSymbol,
  formatSimpleMoney,
  getCurrencyInfo,
  minorUnitsToMajorUnits,
  moneyStringOrNumberToMinorUnits,
} from '../../../react/utils/money';
import { TagadaClient, TagadaClientConfig, TagadaState } from '../../core/client';
import { FunnelState } from '../../core/funnelClient';
import { FunnelAction, FunnelNavigationResult, SimpleFunnelContext } from '../../core/resources/funnel';
import { Environment, EnvironmentConfig } from '../../core/types';
import { RawPluginConfig } from '../../core/utils/pluginConfig';
import { setGlobalApiClient } from '../hooks/useApiQuery';
import { PixelTrackingProvider } from '../hooks/usePixelTracking';

const FunnelScriptInjector = lazy(() => import('../components/FunnelScriptInjector').then(m => ({ default: m.FunnelScriptInjector })));
const DebugDrawer = lazy(() => import('../components/DebugDrawer'));

/**
 * Debug Script Definition
 * Templates can provide debug scripts that will appear in the Debug Drawer "Scripts" tab
 */
export interface DebugScript {
  /** Unique identifier for the script */
  id: string;
  /** Display name shown in the Scripts tab */
  name: string;
  /** Optional description of what the script does */
  description?: string;
  /** The function to execute when the script is run */
  run: (context: TagadaContextValue) => void | Promise<void>;
  /** Optional category to group scripts */
  category?: string;
}

// Professional, subtle loading component for initialization
const InitializationLoader = () => (
  <div
    style={{
      position: 'fixed',
      top: '24px',
      right: '24px',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      padding: '8px 12px',
      backgroundColor: 'rgba(255, 255, 255, 0.9)',
      border: '1px solid rgba(229, 231, 235, 0.8)',
      borderRadius: '8px',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
      backdropFilter: 'blur(8px)',
      zIndex: 999999,
      fontSize: '13px',
      color: '#6b7280',
      fontFamily: 'system-ui, -apple-system, sans-serif',
      fontWeight: '500',
    }}
  >
    <div
      style={{
        width: '12px',
        height: '12px',
        border: '1.5px solid #e5e7eb',
        borderTop: '1.5px solid #9ca3af',
        borderRadius: '50%',
        animation: 'tagada-spin 1s linear infinite',
      }}
    />
    <span>Loading...</span>
    <style>
      {`
        @keyframes tagada-spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}
    </style>
  </div>
);

interface TagadaContextValue extends TagadaState {
  // Underlying core client instance (for advanced usages / other layers)
  client: TagadaClient;
  // API Service (Legacy/Compat)
  apiService: ApiService;

  // Redirect State (set to true when navigating to prevent renders)
  pendingRedirect: boolean;

  // Funnel State & Methods (from FunnelClient)
  funnel: FunnelState & {
    // Current step helper
    currentStep: { id: string } | null;
    // Methods
    next: (event: FunnelAction) => Promise<FunnelNavigationResult>;
    goToStep: (stepId: string) => Promise<FunnelNavigationResult>;
    updateContext: (updates: Partial<SimpleFunnelContext>) => Promise<void>;
    initializeSession: (entryStepId?: string) => Promise<void>;
    endSession: () => Promise<void>;
    retryInitialization: () => Promise<void>;
    refetch: () => Promise<void>;
  };

  // Debug Data
  debugCheckout: {
    isActive: boolean;
    data: any;
    error: Error | null;
    isLoading: boolean;
    lastUpdated: Date | null;
  };
  updateCheckoutDebugData: (data: any, error?: Error | null, isLoading?: boolean) => void;

  debugFunnel: {
    isActive: boolean;
    data: any;
    error: Error | null;
    isLoading: boolean;
    lastUpdated: Date | null;
  };
  updateFunnelDebugData: (data: any, error?: Error | null, isLoading?: boolean) => void;

  // Debug Scripts (provided by templates)
  debugScripts: DebugScript[];

  // Refresh Coordination
  refreshCoordinator: {
    registerCheckoutRefresh: (refreshFn: () => Promise<void>, name?: string) => void;
    registerOrderBumpRefresh: (refreshFn: () => Promise<void>) => void;
    notifyCheckoutChanged: () => Promise<void>;
    notifyOrderBumpChanged: () => Promise<void>;
    unregisterCheckoutRefresh: (refreshFn?: () => Promise<void>) => void;
    unregisterOrderBumpRefresh: (refreshFn?: () => Promise<void>) => void;
  };

  // Money Utilities
  money: {
    formatMoney: typeof formatMoney;
    getCurrencyInfo: typeof getCurrencyInfo;
    moneyStringOrNumberToMinorUnits: typeof moneyStringOrNumberToMinorUnits;
    minorUnitsToMajorUnits: typeof minorUnitsToMajorUnits;
    formatMoneyWithoutSymbol: typeof formatMoneyWithoutSymbol;
    convertCurrency: typeof convertCurrency;
    formatSimpleMoney: typeof formatSimpleMoney;
  };
}

const TagadaContext = createContext<TagadaContextValue | null>(null);

interface TagadaProviderProps {
  children: ReactNode;
  /**
   * Optional environment override.
   * ⚠️ Leave undefined for automatic runtime detection (recommended).
   * Only set this if you need to force a specific environment for testing.
   */
  environment?: Environment;
  customApiConfig?: Partial<EnvironmentConfig>; // Custom API config override for baseUrl and endpoints
  debugMode?: boolean;
  localConfig?: string;
  blockUntilSessionReady?: boolean;
  rawPluginConfig?: RawPluginConfig;
  /**
   * Optional feature flags to enable/disable subsystems (e.g. funnel).
   * By default all features are enabled.
   */
  features?: TagadaClientConfig['features'];

  // Funnel Options
  /**
   * Funnel ID to initialize. If not provided, will be detected from URL query param.
   */
  funnelId?: string;
  /**
   * Auto-initialize funnel session when auth and store are ready.
   * Default: true
   */
  autoInitializeFunnel?: boolean;
  /**
   * Callback fired after funnel navigation
   */
  onNavigate?: (result: FunnelNavigationResult) => void | boolean;
  /**
   * Callback fired on funnel errors
   */
  onFunnelError?: (error: Error) => void;

  /**
   * Debug scripts provided by the template.
   * These will appear in the Debug Drawer "Scripts" tab when debugMode is enabled.
   * Only shown if the array is not empty.
   */
  debugScripts?: DebugScript[];
}

export function TagadaProvider({
  children,
  environment,
  customApiConfig,
  debugMode,
  localConfig,
  blockUntilSessionReady = false,
  rawPluginConfig,
  features,
  funnelId,
  autoInitializeFunnel = true,
  onNavigate,
  onFunnelError,
  debugScripts = [],
}: TagadaProviderProps) {
  // Auto-detect debug mode from environment if not explicitly set
  const effectiveDebugMode =
    debugMode !== undefined
      ? debugMode
      : (typeof import.meta !== 'undefined' && (import.meta as any).env?.DEV) ||
      (typeof process !== 'undefined' && process.env?.NODE_ENV === 'development');

  // Initialize client
  const client = useMemo(() => {
    // Merge features with defaults, ensuring autoRedirect is always false in provider
    const mergedFeatures = features ?? { funnel: true };
    const funnelFeature = mergedFeatures.funnel;
    const config: TagadaClientConfig = {
      environment,
      customApiConfig,
      debugMode: effectiveDebugMode,
      localConfig,
      rawPluginConfig,
      blockUntilSessionReady,
      features: {
        ...mergedFeatures,
        funnel:
          typeof funnelFeature === 'object'
            ? {
              ...funnelFeature,
              // Always disable auto-redirect in core because we handle it in the provider
              // to set pendingRedirect state and prevent UI flashes
              autoRedirect: false,
            }
            : funnelFeature === false
              ? false
              : {
                // Default: enable funnel with auto-redirect disabled
                autoRedirect: false,
              },
      },
    };
    return new TagadaClient(config);
  }, []); // Singleton behavior on first render

  // State Sync
  const [state, setState] = useState<TagadaState>(client.getState());

  useEffect(() => {
    return client.subscribe(setState);
  }, [client]);

  // Listen for config hot-reload updates
  useEffect(() => {
    const cleanup = client.bus.on('CONFIG_UPDATED', () => {
      // Force re-render when config is updated
      setState(client.getState());
      if (state.debugMode) {
        console.log('[TagadaProvider] Config updated, re-rendering...');
      }
    });
    return cleanup;
  }, [client, state.debugMode]);

  // Cleanup client on unmount
  useEffect(() => {
    return () => {
      client.destroy();
    };
  }, [client]);

  // Sync global API client for hooks
  useEffect(() => {
    setGlobalApiClient(client.apiClient);
  }, [client]);

  // Initialize ApiService for backward compatibility
  // We use state.environment to keep it updated
  const [apiService] = useState(
    () =>
      new ApiService({
        environmentConfig: state.environment,
        token: client.apiClient.getCurrentToken(),
      }),
  );

  // Sync token and config to ApiService
  useEffect(() => {
    apiService.updateToken(state.token);
    apiService.updateConfig(state.environment);
  }, [state.token, state.environment, apiService]);

  // Funnel State Management
  const [funnelState, setFunnelState] = useState<FunnelState>(() =>
    client.funnel
      ? client.funnel.getState()
      : {
        context: null,
        isLoading: false,
        isInitialized: false,
        isNavigating: false,
        error: null,
        sessionError: null,
      },
  );

  // Track pending redirect to prevent renders during navigation
  const [pendingRedirect, setPendingRedirect] = useState(false);

  // Subscribe to funnel state changes
  useEffect(() => {
    if (!client.funnel) return;

    // Keep funnel client in sync with latest config/environment
    client.funnel.setConfig({
      pluginConfig: state.pluginConfig,
      environment: state.environment,
    });

    return client.funnel.subscribe(setFunnelState);
  }, [client, state.pluginConfig, state.environment]);

  // Note: Funnel auto-initialization is now handled by TagadaClient itself
  // when the session is initialized. This happens automatically in the core layer.

  // Debug State (React specific)
  const [debugCheckout, setDebugCheckout] = useState<{
    isActive: boolean;
    data: any;
    error: Error | null;
    isLoading: boolean;
    lastUpdated: Date | null;
  }>({
    isActive: false,
    data: null,
    error: null,
    isLoading: false,
    lastUpdated: null,
  });

  const [debugFunnel, setDebugFunnel] = useState<{
    isActive: boolean;
    data: any;
    error: Error | null;
    isLoading: boolean;
    lastUpdated: Date | null;
  }>({
    isActive: false,
    data: null,
    error: null,
    isLoading: false,
    lastUpdated: null,
  });

  // Debug Data Updaters
  const updateCheckoutDebugData = useCallback((data: any, error?: Error | null, isLoading?: boolean) => {
    setDebugCheckout({
      isActive: true,
      data,
      error: error ?? null,
      isLoading: isLoading ?? false,
      lastUpdated: new Date(),
    });
  }, []);

  const updateFunnelDebugData = useCallback((data: any, error?: Error | null, isLoading?: boolean) => {
    setDebugFunnel({
      isActive: true,
      data,
      error: error ?? null,
      isLoading: isLoading ?? false,
      lastUpdated: new Date(),
    });
  }, []);

  // Refresh Coordinator (backed by EventBus in core)
  const refreshCoordinator = useMemo(
    () => ({
      registerCheckoutRefresh: (refreshFn: () => Promise<void>, name?: string) => {
        client.bus.on('CHECKOUT_REFRESH', refreshFn);
      },
      registerOrderBumpRefresh: (refreshFn: () => Promise<void>) => {
        client.bus.on('ORDER_BUMP_REFRESH', refreshFn);
      },
      notifyCheckoutChanged: async () => {
        await client.bus.emit('ORDER_BUMP_REFRESH'); // Notify bumps that checkout changed? Wait logic
      },
      notifyOrderBumpChanged: async () => {
        await client.bus.emit('CHECKOUT_REFRESH'); // Notify checkout that bumps changed
      },
      unregisterCheckoutRefresh: (refreshFn?: () => Promise<void>) => {
        if (refreshFn) client.bus.off('CHECKOUT_REFRESH', refreshFn);
      },
      unregisterOrderBumpRefresh: (refreshFn?: () => Promise<void>) => {
        if (refreshFn) client.bus.off('ORDER_BUMP_REFRESH', refreshFn);
      },
    }),
    [client],
  );

  // Money Utils
  const moneyUtils = useMemo(
    () => ({
      formatMoney,
      getCurrencyInfo,
      moneyStringOrNumberToMinorUnits,
      minorUnitsToMajorUnits,
      formatMoneyWithoutSymbol,
      convertCurrency,
      formatSimpleMoney,
    }),
    [],
  );

  const [isDebugDrawerOpen, setIsDebugDrawerOpen] = useState(false);

  // Track last injected script to prevent duplicate execution
  /**
   * Editor Element Click Bridge (Iframe → Parent)
   *
   * When running inside the CRM config editor iframe, elements can declare an `editor-id`
   * attribute (e.g. <h1 editor-id="config.texts.checkoutTitle">Title</h1>).
   *
   * This effect sets up a delegated click listener on `document` that:
   * - Finds the closest ancestor with an `editor-id`
   * - Sends a `postMessage` to `window.parent` with the editorId and text content
   *
   * The parent (ConfigEditor) can then map this back to the corresponding config field.
   */
  useEffect(() => {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      return;
    }

    const handleClick = (event: MouseEvent) => {
      if (!(event.target instanceof Element)) return;

      const targetWithEditorId = event.target.closest('[editor-id]') as HTMLElement | null;
      if (!targetWithEditorId) return;

      const editorId = targetWithEditorId.getAttribute('editor-id');
      if (!editorId) return;

      const textContent = targetWithEditorId.textContent ?? '';

      // Only attempt cross-window messaging when inside an iframe
      const isInIframe = window.parent && window.parent !== window;
      if (!isInIframe) {
        return;
      }

      const message = {
        type: 'TAGADAPAY_EDITOR_ELEMENT_CLICK' as const,
        editorId,
        textContent,
      };

      try {
        // Derive parent origin from document.referrer when possible; fall back to '*'
        // This avoids postMessage origin mismatch errors in dev (e.g. 3001 ↔ 5173)
        let targetOrigin = '*';
        try {
          if (typeof document !== 'undefined' && document.referrer) {
            const url = new URL(document.referrer);
            targetOrigin = url.origin;
          }
        } catch {
          // Fallback to '*'
          targetOrigin = '*';
        }

        window.parent.postMessage(message, targetOrigin);

        if (state.debugMode) {
          // eslint-disable-next-line no-console
          console.log('[TagadaProvider] Sent editor element click to parent:', message);
        }
      } catch (error) {
        if (state.debugMode) {
          // eslint-disable-next-line no-console
          console.error('[TagadaProvider] Failed to post editor element click message:', error);
        }
      }
    };

    document.addEventListener('click', handleClick, true);

    return () => {
      document.removeEventListener('click', handleClick, true);
    };
  }, [state.debugMode]);

  // Funnel Methods
  const funnelMethods = useMemo(() => {
    if (!client.funnel) {
      // Return no-op methods if funnel is disabled
      const noopAsync = async () => {
        throw new Error('Funnel feature is disabled');
      };
      return {
        next: noopAsync as any,
        goToStep: noopAsync as any,
        updateContext: noopAsync,
        initializeSession: noopAsync,
        endSession: noopAsync,
        retryInitialization: noopAsync,
        refetch: noopAsync,
      };
    }

    // Helper to handle navigation result with optional auto-redirect
    const handleNavigationResult = (result: FunnelNavigationResult) => {
      // Call custom onNavigate handler if provided
      let shouldAutoRedirect = true;
      if (onNavigate) {
        const handlerResult = onNavigate(result);
        // If handler explicitly returns false, skip auto-redirect
        if (handlerResult === false) {
          shouldAutoRedirect = false;
        }
      }

      // Default behavior: auto-redirect if result has a URL
      if (shouldAutoRedirect && result?.url && typeof window !== 'undefined') {
        console.log('🚀 [TagadaProvider] Auto-redirecting to:', result.url);
        // Set pending redirect flag BEFORE navigation to prevent renders
        setPendingRedirect(true);
        window.location.replace(result.url);
      }
    };

    const next = async (event: FunnelAction, options?: { waitForSession?: boolean }) => {
      const result = await client.funnel!.navigate(event, options);
      handleNavigationResult(result);
      return result;
    };

    const goToStep = async (stepId: string) => {
      const result = await client.funnel!.goToStep(stepId);
      handleNavigationResult(result);
      return result;
    };

    return {
      next,
      goToStep,
      updateContext: (updates: Partial<SimpleFunnelContext>) => client.funnel!.updateContext(updates),
      initializeSession: async (entryStepId?: string) => {
        const accountId = client.getAccountId();
        if (!accountId || !state.auth.session || !state.store) {
          throw new Error('Cannot initialize funnel: missing required data (accountId, session, or store)');
        }
        await client.funnel!.initialize(
          { customerId: state.auth.session.customerId, sessionId: state.auth.session.sessionId },
          { id: state.store.id, accountId },
          funnelId,
          entryStepId,
        );
      },
      endSession: () => client.funnel!.endSession(),
      retryInitialization: () => {
        const accountId = client.getAccountId();
        if (!accountId || !state.auth.session || !state.store) {
          return Promise.reject(new Error('Cannot retry initialization: missing required data'));
        }
        return client
          .funnel!.autoInitialize(
            { customerId: state.auth.session.customerId, sessionId: state.auth.session.sessionId },
            { id: state.store.id, accountId },
            funnelId,
          )
          .then(() => { });
      },
      refetch: async () => {
        await client.funnel!.refreshSession();
      },
    };
  }, [client, state.auth.session, state.store, funnelId, onNavigate]);

  const contextValue: TagadaContextValue = {
    client,
    ...state,
    apiService,
    pendingRedirect,
    funnel: {
      ...funnelState,
      currentStep: funnelState.context?.currentStepId ? { id: funnelState.context.currentStepId } : null,
      ...funnelMethods,
    },
    debugCheckout,
    updateCheckoutDebugData,
    debugFunnel,
    updateFunnelDebugData,
    debugScripts,
    refreshCoordinator,
    money: moneyUtils,
  };

  // Query Client
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            staleTime: 30_000,
            gcTime: 5 * 60 * 1000,
            refetchOnWindowFocus: false,
            retry: 1,
          },
          mutations: {
            retry: 1,
          },
        },
      }),
  );

  // Loading State Logic
  const shouldShowLoading =
    state.isLoading || state.pluginConfigLoading || (blockUntilSessionReady && !state.isSessionInitialized);
  const canRenderChildren =
    !state.pluginConfigLoading && (!blockUntilSessionReady || state.isSessionInitialized);

  return (
    <QueryClientProvider client={queryClient}>
      <TagadaContext.Provider value={contextValue}>
        <PixelTrackingProvider>
          <Suspense fallback={null}><FunnelScriptInjector {...funnelState} /></Suspense>

          {shouldShowLoading && <InitializationLoader />}

          {state.debugMode && canRenderChildren && (
            <>
              <button
                onClick={() => setIsDebugDrawerOpen(true)}
                style={{
                  position: 'fixed',
                  bottom: '16px',
                  right: '16px',
                  backgroundColor: '#f97316',
                  color: 'white',
                  border: 'none',
                  borderRadius: '50%',
                  width: '56px',
                  height: '56px',
                  fontSize: '20px',
                  cursor: 'pointer',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
                  zIndex: 999997,
                  transition: 'all 0.2s ease',
                }}
                title="Open TagadaPay SDK Debug Panel"
              >
                🐛
              </button>
              <Suspense fallback={null}>
                <DebugDrawer isOpen={isDebugDrawerOpen} onClose={() => setIsDebugDrawerOpen(false)} />
              </Suspense>
            </>
          )}

          {canRenderChildren && children}
        </PixelTrackingProvider>
      </TagadaContext.Provider>
    </QueryClientProvider>
  );
}

export function useTagadaContext(): TagadaContextValue {
  const context = useContext(TagadaContext);
  if (!context) {
    throw new Error('useTagadaContext must be used within a TagadaProvider');
  }
  return context;
}
